import { AbstractComponent, ComponentConnectedParams, componentsRegistryService, RxBucket, routerService } from "cruzo";
import { arrayToHash } from "cruzo/utils";
import "cruzo/ui-components/spinner";
import { SpinnerComponent, SpinnerConfig, SpinnerValue } from "cruzo/ui-components/spinner";

type SectionItem = {
  id: string;
  title: string;
  text: string;
  age: string;
  img: string;
};

enum WorkspaceTab {
  "services" = "services",
  "opensource" = "opensource",
  "apps" = "apps",
};

const sections: SectionItem[] = [
  {
    id: "value",
    title: "Ценность",
    text: "Определяем и формируем пользу вашего сервиса для ваших клиентов",
    age: "Работа с мотивацией пользователя",
    img: "circle",
  },
  {
    id: "visual",
    title: "Визуал",
    text: "Собираем отличительную и минималистичную идею для отображения вашего сервиса",
    age: "Необычный и красивый",
    img: "cube",
  },
  {
    id: "scenarios",
    title: "Сценарии",
    text: "Строим продукт с понятным поведением для пользователя",
    age: "Проработка взаимодействия",
    img: "icon",
  },
  {
    id: "tech",
    title: "Архитектура",
    text: "Проработка систем вашего сервиса для легкой масштабируемости и разработки",
    age: "Выбор оптимальных технологий",
    img: "triangle",
  },
];

const sectionsHash = arrayToHash(sections, 'id');

type OpenSourceItem = {
  title: string;
  tag: string;
  text: string;
  url: string;
};

const opensourceItems: OpenSourceItem[] = [
  {
    title: "cruzo",
    tag: "UI framework",
    text: "Компактный реактивный UI: шаблонный движок, Rx без VDOM и лишних зависимостей.",
    url: "https://github.com/MaratBektemirov/cruzo",
  },
  {
    title: "alif",
    tag: "Fuzzy search",
    text: "Быстрый нечёткий поиск и приблизительное сопоставление слов, zero-dependency.",
    url: "https://github.com/MaratBektemirov/alif",
  },
];

type TabItem = {
  title: string;
  id: string;
};

const tabsItems: TabItem[] = [
  {
    title: 'Что мы делаем',
    id: WorkspaceTab.services,
  },
  {
    title: 'Open-source',
    id: WorkspaceTab.opensource,
  },
  {
    title: 'Наши приложения',
    id: WorkspaceTab.apps,
  },
]

export class SiteRootComponent extends AbstractComponent {
  static selector = "site-root";

  sections$ = this.newRx<SectionItem[]>(sections);
  sectionsHash$ = this.newRx(sectionsHash);
  opensourceItems$ = this.newRx<OpenSourceItem[]>(opensourceItems);
  tabs$ = this.newRx<TabItem[]>(tabsItems);
  activeTab$ = this.newRx<WorkspaceTab>(WorkspaceTab.services);
  activeSection$ = this.newRx<string>(sections[0].id);
  sectionContentAttached$ = this.newRx(true);
  sectionImageSrc$ = this.newRx("");
  sectionImageAttached$ = this.newRx(false);

  dependencies = new Set([SpinnerComponent.selector]);

  innerBucket = new RxBucket({
    section_image_spinner: {
      config: SpinnerConfig({
        color: "#202020",
        size: "8px",
      }),
    },
  });

  selectSection(id: string) {
    if (this.activeSection$.actual === id) return;
    this.sectionContentAttached$.update(false);
    this.activeSection$.update(id);
    queueMicrotask(() => {
      this.sectionContentAttached$.update(true);
    });
  }

  navigateToTab(id: WorkspaceTab) {
    this.activeTab$.update(id);
    routerService.pushHistory(`#${id}`);
    this.scrollToTabPanel(id);
  }

  private scrollToTabPanel(tabId: string) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = this.node.querySelector(`#${tabId}`);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }

  private loadSectionImage(sectionId: string) {
    const section = sectionsHash[sectionId];
    if (!section) return;

    const src = `/assets/${section.img}.png`;
    if (this.sectionImageSrc$.actual === src && this.sectionImageAttached$.actual) {
      this.innerBucket.setValue("section_image_spinner", SpinnerValue.inactive);
      return;
    }

    this.innerBucket.setValue("section_image_spinner", SpinnerValue.active);

    const loader = new Image();
    loader.onload = () => {
      if (this.activeSection$.actual !== sectionId) return;
      this.sectionImageSrc$.update(src);
      this.sectionImageAttached$.update(false);
      queueMicrotask(() => {
        this.sectionImageAttached$.update(true);
      });
      this.innerBucket.setValue("section_image_spinner", SpinnerValue.inactive);
    };
    loader.onerror = () => {
      if (this.activeSection$.actual !== sectionId) return;
      this.innerBucket.setValue("section_image_spinner", SpinnerValue.inactive);
    };
    loader.src = src;
  }

  connectedCallback(params?: ComponentConnectedParams): void {
    this.newRxFunc((routeParams) => {
      if (!routeParams.section) return

      this.activeTab$.update(routeParams.section)
      console.log(routeParams)
    }, params.routeParams$)

    super.connectedCallback(params);

    this.newRxFunc((sectionId) => {
      this.loadSectionImage(sectionId);
    }, this.activeSection$);
  }

  getHTML() {
    return `
      <div class="page">
        <main id="top" class="frame">
          <header class="topbar">
            <a class="brand" href="#top" aria-label="900k home">
              <svg
                class="brand-logo-svg"
                width="54"
                height="54"
                viewBox="0 0 180 180"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
                >
                <path
                  class="brand-logo-p"
                  fill="currentColor"
                  d="M34.7799 98.1761C35.1533 102.189 36.5533 105.456 38.9799 107.976C41.4999 110.403 44.8599 111.616 49.0599 111.616C53.3533 111.616 56.7599 110.496 59.2799 108.256C61.8933 105.923 63.8999 103.076 65.2999 99.7161C66.6999 96.2628 67.6333 92.6228 68.0999 88.7961C68.6599 84.8761 69.0799 81.2828 69.3599 78.0161L69.0799 77.7361C66.9333 81.5628 63.9933 84.5028 60.2599 86.5561C56.5266 88.5161 52.4199 89.4961 47.9399 89.4961C43.2733 89.4961 39.0266 88.7028 35.1999 87.1161C31.4666 85.5294 28.2466 83.3361 25.5399 80.5361C22.8333 77.6428 20.7333 74.1894 19.2399 70.1761C17.7466 66.0694 16.9999 61.4961 16.9999 56.4561C16.9999 51.6961 17.7466 47.3094 19.2399 43.2961C20.7333 39.1894 22.9266 35.6428 25.8199 32.6561C28.7133 29.5761 32.2599 27.1961 36.4599 25.5161C40.6599 23.8361 45.4199 22.9961 50.7399 22.9961C57.6466 22.9961 63.3399 24.3494 67.8199 27.0561C72.2999 29.6694 75.7999 33.3094 78.3199 37.9761C80.9333 42.5494 82.7533 47.9628 83.7799 54.2161C84.8066 60.4694 85.3199 67.1428 85.3199 74.2361C85.3199 80.1161 84.6666 86.0428 83.3599 92.0161C82.1466 97.9894 80.0933 103.449 77.1999 108.396C74.3066 113.249 70.4799 117.216 65.7199 120.296C61.0533 123.376 55.2666 124.916 48.3599 124.916C44.4399 124.916 40.7533 124.263 37.2999 122.956C33.8466 121.649 30.7666 119.829 28.0599 117.496C25.4466 115.163 23.2999 112.363 21.6199 109.096C19.9399 105.829 19.0066 102.189 18.8199 98.1761H34.7799ZM50.0399 36.2961C47.1466 36.2961 44.6266 36.8561 42.4799 37.9761C40.3333 39.0028 38.5599 40.4494 37.1599 42.3161C35.7599 44.1828 34.6866 46.3294 33.9399 48.7561C33.2866 51.1828 32.9599 53.7028 32.9599 56.3161C32.9599 59.0228 33.2866 61.6361 33.9399 64.1561C34.6866 66.5828 35.7599 68.7294 37.1599 70.5961C38.5599 72.4628 40.3333 74.0028 42.4799 75.2161C44.6266 76.3361 47.1466 76.8961 50.0399 76.8961C53.0266 76.8961 55.6399 76.3361 57.8799 75.2161C60.1199 74.0961 61.9399 72.6028 63.3399 70.7361C64.8333 68.7761 65.9533 66.5828 66.6999 64.1561C67.4466 61.6361 67.8199 59.0228 67.8199 56.3161C67.8199 53.7028 67.4466 51.1828 66.6999 48.7561C65.9533 46.3294 64.8333 44.2294 63.3399 42.4561C61.9399 40.5894 60.1199 39.0961 57.8799 37.9761C55.6399 36.8561 53.0266 36.2961 50.0399 36.2961Z"
                  />
                <path
                  class="brand-logo-p"
                  fill="currentColor"
                  d="M90.1599 36.9966C97.1599 36.9966 102.853 38.6299 107.24 41.8966C111.72 45.0699 115.173 49.1766 117.6 54.2166C120.12 59.1633 121.8 64.6232 122.64 70.5966C123.573 76.5699 124.04 82.3566 124.04 87.9566C124.04 93.5566 123.573 99.3433 122.64 105.317C121.8 111.29 120.12 116.797 117.6 121.837C115.173 126.783 111.72 130.89 107.24 134.157C102.853 137.33 97.1599 138.917 90.1599 138.917C83.1599 138.917 77.4199 137.33 72.9399 134.157C68.5532 130.89 65.0999 126.783 62.5799 121.837C60.1532 116.797 58.4732 111.29 57.5399 105.317C56.6999 99.3433 56.2799 93.5566 56.2799 87.9566C56.2799 82.3566 56.6999 76.5699 57.5399 70.5966C58.4732 64.6232 60.1532 59.1633 62.5799 54.2166C65.0999 49.1766 68.5532 45.0699 72.9399 41.8966C77.4199 38.6299 83.1599 36.9966 90.1599 36.9966ZM90.1599 125.617C93.6132 125.617 96.5066 124.637 98.8399 122.677C101.173 120.623 102.993 117.917 104.3 114.557C105.7 111.103 106.68 107.09 107.24 102.517C107.8 97.9433 108.08 93.0899 108.08 87.9566C108.08 82.8233 107.8 78.0166 107.24 73.5366C106.68 68.9633 105.7 64.9499 104.3 61.4966C102.993 58.0432 101.173 55.3366 98.8399 53.3766C96.5066 51.3233 93.6132 50.2966 90.1599 50.2966C86.6132 50.2966 83.6732 51.3233 81.3399 53.3766C79.0999 55.3366 77.2799 58.0432 75.8799 61.4966C74.5732 64.9499 73.6399 68.9633 73.0799 73.5366C72.5199 78.0166 72.2399 82.8233 72.2399 87.9566C72.2399 93.0899 72.5199 97.9433 73.0799 102.517C73.6399 107.09 74.5732 111.103 75.8799 114.557C77.2799 117.917 79.0999 120.623 81.3399 122.677C83.6732 124.637 86.6132 125.617 90.1599 125.617Z"
                  />
                <path
                  class="brand-logo-p"
                  fill="currentColor"
                  d="M129.16 54.9971C136.16 54.9971 141.853 56.6304 146.24 59.8971C150.72 63.0704 154.173 67.1771 156.6 72.2171C159.12 77.1637 160.8 82.6237 161.64 88.5971C162.573 94.5704 163.04 100.357 163.04 105.957C163.04 111.557 162.573 117.344 161.64 123.317C160.8 129.29 159.12 134.797 156.6 139.837C154.173 144.784 150.72 148.89 146.24 152.157C141.853 155.33 136.16 156.917 129.16 156.917C122.16 156.917 116.42 155.33 111.94 152.157C107.553 148.89 104.1 144.784 101.58 139.837C99.1532 134.797 97.4732 129.29 96.5399 123.317C95.6999 117.344 95.2799 111.557 95.2799 105.957C95.2799 100.357 95.6999 94.5704 96.5399 88.5971C97.4732 82.6237 99.1532 77.1637 101.58 72.2171C104.1 67.1771 107.553 63.0704 111.94 59.8971C116.42 56.6304 122.16 54.9971 129.16 54.9971ZM129.16 143.617C132.613 143.617 135.507 142.637 137.84 140.677C140.173 138.624 141.993 135.917 143.3 132.557C144.7 129.104 145.68 125.09 146.24 120.517C146.8 115.944 147.08 111.09 147.08 105.957C147.08 100.824 146.8 96.0171 146.24 91.5371C145.68 86.9637 144.7 82.9504 143.3 79.4971C141.993 76.0437 140.173 73.3371 137.84 71.3771C135.507 69.3237 132.613 68.2971 129.16 68.2971C125.613 68.2971 122.673 69.3237 120.34 71.3771C118.1 73.3371 116.28 76.0437 114.88 79.4971C113.573 82.9504 112.64 86.9637 112.08 91.5371C111.52 96.0171 111.24 100.824 111.24 105.957C111.24 111.09 111.52 115.944 112.08 120.517C112.64 125.09 113.573 129.104 114.88 132.557C116.28 135.917 118.1 138.624 120.34 140.677C122.673 142.637 125.613 143.617 129.16 143.617Z"
                  />
              </svg>
              <span>Бюро 900K</span>
            </a>
            <nav class="nav">
              <a
                repeat="{{ root.tabs$::rx }}"
                class="{{root.activeTab$::rx === this.id ? 'active' : ''}}"
                href="#{{ this.id }}"
                onclick="{{ root.navigateToTab(this.id) }}">{{ this.title }}</a>
            </nav>
          </header>

          <section class="big-title">
            <h1>Development</h1>
          </section>

          <section class="headline">
            <p>Развитие ваших сервисов, приложений и информационных ресурсов</p>
          </section>

          <section class="workspace" id="workspace">
            <div class="workspace-main">
              <h3>Направления</h3>
              <p class="workspace-sub">Основная деятельность бюро 900k</p>
              <div class="tabs" aria-label="Workspace sections">
                <span repeat="{{ root.tabs$::rx }}"
                  onclick="{{ root.activeTab$.update(this.id) }}"
                  class="tab {{root.activeTab$::rx === this.id ? 'tab-active' : ''}}">
                  {{this.title}}
                </span>
              </div>

              <div class="tab-panels">
                <div id="services" class="tab-panel tab-panel_services" role="tabpanel" attached="{{ root.activeTab$::rx === 'services' }}">
                  <div class="section-tabs" aria-label="Service sections">
                    <span
                      repeat="{{ root.sections$::rx }}"
                      onclick="{{ root.selectSection(this.id) }}"
                      class="section-tab {{ root.activeSection$::rx === this.id ? 'section-tab-active' : '' }}">
                      {{ this.title }}
                    </span>
                  </div>
                  <article let-section="{{ root.sectionsHash$::rx[root.activeSection$::rx] }}"
                    class="service-line"
                    id="{{ section.id }}">
                    <div class="service-line-icon-col">
                      <div
                        class="service-line-icon"
                        is="spinner"
                        component-id="section_image_spinner"
                        bucket-id="${this.innerBucket.id}">
                        <div
                          class="section-image"
                          attached="{{ root.sectionImageAttached$::rx }}">
                          <img
                            class="service-line-icon-image service-line-icon-image_{{section.img}}"
                            src="{{ root.sectionImageSrc$::rx }}"
                            alt=""/>
                        </div>
                      </div>
                    </div>
                    <div class="service-line__text">
                      <div
                        class="section-content"
                        attached="{{ root.sectionContentAttached$::rx }}">
                        <h2>{{ section.title }} <small>{{ section.age }}</small></h2>
                        <p>{{ section.text }}</p>
                      </div>
                    </div>
                  </article>
                </div>

                <div id="opensource" class="tab-panel" role="tabpanel" attached="{{ root.activeTab$::rx === 'opensource' }}">
                  <div class="service-lines">
                    <article repeat="{{ root.opensourceItems$::rx }}" class="service-line">
                      <div></div>
                      <div>
                        <h2>
                          <a href="{{ this.url }}" target="_blank" rel="noreferrer noopener">{{ this.title }}</a>
                          <small>{{ this.tag }}</small>
                        </h2>
                        <p>{{ this.text }}</p>
                      </div>
                    </article>
                  </div>
                </div>

                <div id="apps" class="tab-panel" role="tabpanel" attached="{{ root.activeTab$::rx === 'apps' }}">
                  <div class="service-lines">
                    <article class="service-line">
                      <div></div>
                      <div>
                        <h2>Наши приложения <small>В разработке</small></h2>
                        <p>Скоро здесь появятся кейсы, ссылки на релизы и демо.</p>
                      </div>
                    </article>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <footer class="about">
            <div class="footer-powered">
              <a
                href="https://github.com/MaratBektemirov/cruzo"
                class="footer-powered-link"
                target="_blank"
                rel="noreferrer noopener"
                >
                <img src="/assets/cruzo-logo.svg" alt="" class="footer-powered-logo" />
                <span class="footer-powered-text">Powered by Cruzo</span>
              </a>
            </div>
          </footer>
        </main>
      </div>
`;
  }
}

componentsRegistryService.define(SiteRootComponent);
